import { Type, type Static, type TSchema, type TObject } from "@sinclair/typebox";
import { CancelledBy, Cents, DriverStatus, LatLng, PaymentMethod, Place, RideCategory, TripKind, TripStatus } from "./domain.ts";
import { SafetyIncidentKind, SafetyIncidentStatus } from "./next.ts";
import { OfferDelivery } from "./delivery.ts";
import { TripCollectionView } from "./pix.ts";
import { SupportMessageView, SupportTicketStatus } from "./support.ts";

/**
 * Every realtime event is `{ type, v, id, occurredAt, data }`.
 * Native apps decode these from generated code (JSON Schema → quicktype), so changes must be
 * additive within a version; breaking changes bump `v`.
 */
function event<T extends string, D extends TSchema, V extends number = 1>(type: T, data: D, v: V = 1 as V) {
  return Type.Object(
    {
      type: Type.Literal(type),
      v: Type.Literal(v),
      id: Type.String({ description: "Event id (uuidv7), also the idempotency key" }),
      occurredAt: Type.String({ format: "date-time" }),
      data,
    },
    { $id: `${type}.v${v}` },
  );
}

/** Nested object schemas carry an `$id` so native codegen gives them one stable name each (ADR 0006). */
export const VehicleCard = Type.Object({ model: Type.String(), color: Type.String(), plate: Type.String() }, { $id: "VehicleCard" });

export const DriverCard = Type.Object(
  {
    driverId: Type.String(),
    firstName: Type.String(),
    /** The driver's profile photo as a short-lived link (ADR 0012); absent without one: show initials. */
    photoUrl: Type.Optional(Type.String()),
    rating: Type.Number(),
    vehicle: VehicleCard,
    /** Staff confirmed the driver's prova de vida (their latest video is approved): the rider app's blue check. */
    identityVerified: Type.Optional(Type.Boolean()),
  },
  { $id: "DriverCard" },
);

export const TripRequested = event(
  "trip.requested",
  Type.Object({ tripId: Type.String(), riderId: Type.String(), category: RideCategory, pickup: Place, dropoff: Place, fareCents: Cents }),
);

/** Sent to one driver at a time on `user:#{driverId}` (ADR 0005). */
export const TripOffered = event(
  "trip.offered",
  Type.Object({
    tripId: Type.String(),
    offerId: Type.String(),
    expiresAt: Type.String({ format: "date-time" }),
    category: RideCategory,
    /** The type's name ("Conforto"), for the offer chip. */
    categoryName: Type.String(),
    riderFirstName: Type.String(),
    riderRating: Type.Number(),
    /** The rider's completed trips, next to the rating: "5,0 (200)". */
    riderCompletedTrips: Type.Optional(Type.Integer()),
    pickup: Place,
    dropoff: Place,
    pickupEtaSeconds: Type.Integer(),
    pickupDistanceMeters: Type.Integer(),
    tripDurationSeconds: Type.Integer(),
    tripDistanceMeters: Type.Integer(),
    fareCents: Cents,
    driverEarningsCents: Cents,
    surgeMultiplier: Type.Number(),
    /** card: charged after the trip (the driver is paid either way). */
    paymentMethod: PaymentMethod,
    /** Phase 2; absent means "ride". */
    kind: Type.Optional(TripKind),
    /** Deliveries only: "Entrega · Pequeno · Documentos". */
    delivery: Type.Optional(OfferDelivery),
    /** Street route pickup → dropoff (encoded polyline, precision 5); absent when none could be computed. */
    polyline: Type.Optional(Type.String()),
    /** Paradas, in order. */
    stops: Type.Optional(Type.Array(Place)),
    /** Levar pet: "Com pet" badge; the fee (all to the driver) is in driverEarningsCents. */
    hasPet: Type.Optional(Type.Boolean()),
    petFeeCents: Type.Optional(Cents),
    /** Agendar: the pickup time the rider booked. */
    scheduledFor: Type.Optional(Type.String({ format: "date-time" })),
  }),
);

export const TripOfferClosed = event(
  "trip.offer_closed",
  Type.Object({
    tripId: Type.String(),
    offerId: Type.String(),
    reason: Type.Union([Type.Literal("expired"), Type.Literal("declined"), Type.Literal("taken"), Type.Literal("cancelled")]),
  }),
);

/**
 * "Corridas abertas": a trip the sequential dispatch hasn't matched yet, as the driver's list shows
 * it. Until a driver accepts, only the pickup and dropoff areas (bairros) and points rounded to
 * about 100 m are shown.
 */
export const OpenTrip = Type.Object(
  {
    tripId: Type.String(),
    listedAt: Type.String({ format: "date-time" }),
    category: RideCategory,
    categoryName: Type.String(),
    riderFirstName: Type.String(),
    riderRating: Type.Number(),
    /** The rider's completed trips, next to the rating: "5,0 (200)". */
    riderCompletedTrips: Type.Optional(Type.Integer()),
    /** The bairro ("Bela Vista"). */
    pickupArea: Type.String(),
    dropoffArea: Type.String(),
    /** Rounded to about 100 m. */
    pickupLocation: LatLng,
    dropoffLocation: LatLng,
    /** Straight line from the driver's last position; the real ETA comes on accept. */
    pickupDistanceMeters: Type.Integer(),
    tripDurationSeconds: Type.Integer(),
    tripDistanceMeters: Type.Integer(),
    driverEarningsCents: Cents,
    surgeMultiplier: Type.Number(),
    paymentMethod: PaymentMethod,
    /** Pagar na hora: what the driver collects from the rider ("Receber R$ 20,00"). Cash trips only. */
    collectCents: Type.Optional(Cents),
    kind: Type.Optional(TripKind),
    delivery: Type.Optional(OfferDelivery),
    /** Paradas on the way. */
    stopCount: Type.Optional(Type.Integer({ minimum: 1 })),
    hasPet: Type.Optional(Type.Boolean()),
    scheduledFor: Type.Optional(Type.String({ format: "date-time" })),
  },
  { $id: "OpenTrip" },
);

/** A trip joined the driver's Corridas abertas (on `user:#{driverId}`). */
export const OpenTripListed = event("open_trip.listed", OpenTrip);

/** A trip left Corridas abertas: someone accepted it, or it was cancelled or ran out of search time. */
export const OpenTripRemoved = event("open_trip.removed", Type.Object({ tripId: Type.String() }));

export const TripStatusChanged = event(
  "trip.status_changed",
  Type.Object({
    tripId: Type.String(),
    status: TripStatus,
    previous: TripStatus,
    driver: Type.Optional(DriverCard),
    pickupEtaSeconds: Type.Optional(Type.Integer()),
    cancelledBy: Type.Optional(CancelledBy),
    cancellationFeeCents: Type.Optional(Cents),
  }),
);

/** Published on `trip:{id}` while the trip is active. */
export const DriverLocation = event(
  "driver.location",
  Type.Object({
    tripId: Type.String(),
    location: LatLng,
    heading: Type.Optional(Type.Number()),
    speedMps: Type.Optional(Type.Number()),
    etaSeconds: Type.Optional(Type.Integer()),
    recordedAt: Type.String({ format: "date-time" }),
  }),
);

export const ChatMessage = event(
  "chat.message",
  Type.Object({
    tripId: Type.String(),
    messageId: Type.String(),
    senderId: Type.String(),
    senderRole: Type.Union([Type.Literal("rider"), Type.Literal("driver")]),
    body: Type.String({ maxLength: 1000 }),
    sentAt: Type.String({ format: "date-time" }),
  }),
);

export const ChatRead = event(
  "chat.read",
  Type.Object({ tripId: Type.String(), readerId: Type.String(), upToMessageId: Type.String() }),
);

export const PixCharge = event(
  "payment.pix_created",
  Type.Object({
    tripId: Type.String(),
    paymentId: Type.String(),
    amountCents: Cents,
    qrCode: Type.String({ description: "Pix copia-e-cola" }),
    qrCodeBase64: Type.String(),
    expiresAt: Type.String({ format: "date-time" }),
  }),
);

export const PaymentConfirmed = event(
  "payment.confirmed",
  Type.Object({ tripId: Type.String(), paymentId: Type.String(), amountCents: Cents, method: Type.Literal("pix") }),
);

/** Card trips: the fare (+ tip) was charged, to `user:#{riderId}`. */
export const CardCharged = event(
  "payment.card_charged",
  Type.Object({ tripId: Type.String(), paymentId: Type.String(), amountCents: Cents, cardLabel: Type.String() }),
);

/** A card charge was declined, to `user:#{riderId}`. `final`: retries are over and it became a debt to pay by Pix. */
export const CardFailed = event(
  "payment.card_failed",
  Type.Object({ tripId: Type.String(), paymentId: Type.String(), amountCents: Cents, attempts: Type.Integer(), final: Type.Boolean(), message: Type.String() }),
);

/** A rider tipped, to `user:#{driverId}` once the tip is paid (once per tip). `riderFirstName` is empty when the rider has no usable name. */
export const TipReceived = event(
  "tip.received",
  Type.Object({ tripId: Type.String(), amountCents: Cents, riderFirstName: Type.String() }),
);

/**
 * Pagar na hora: a cash trip's collection opened or changed (the driver showed a Pix, it was paid,
 * or it was settled), on `trip:{id}` and to both participants. The rider app mirrors `pix`.
 */
export const TripCollectionUpdated = event("trip.collection_updated", TripCollectionView);

/** The driver reached or left a stop, on `trip:{id}` and `user:#{riderId}`. */
export const TripStopUpdated = event(
  "trip.stop_updated",
  Type.Object({
    tripId: Type.String(),
    seq: Type.Integer({ minimum: 1 }),
    arrivedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    departedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
  }),
);

/**
 * The car is about a minute (`trip.approaching_seconds`) or `trip.approaching_meters` from where
 * it is going: the pickup ("Seu motorista está chegando", pushed), a stop or the destination
 * ("Chegando ao destino", in the app). Once per target, on `trip:{id}` and `user:#{riderId}`.
 */
export const TripDriverApproaching = event(
  "trip.driver_approaching",
  Type.Object({
    tripId: Type.String(),
    target: Type.Union([Type.Literal("pickup"), Type.Literal("stop"), Type.Literal("dropoff")]),
    /** target "stop": which one (1-based). */
    stopSeq: Type.Optional(Type.Integer({ minimum: 1 })),
    etaSeconds: Type.Optional(Type.Integer({ minimum: 0 })),
  }),
);

/** Agendar: `scheduled.reminder_minutes` before the pickup, to `user:#{riderId}`. */
export const TripScheduledReminder = event(
  "trip.scheduled_reminder",
  Type.Object({ tripId: Type.String(), scheduledFor: Type.String({ format: "date-time" }), minutesBefore: Type.Integer() }),
);

/** A new SOS or incident report, to `admin:live` (Segurança banner + sound). Ids and positions only. */
export const SafetyIncidentEvent = event(
  "safety.incident",
  Type.Object({
    incidentId: Type.String(),
    kind: SafetyIncidentKind,
    cityId: Type.Union([Type.String(), Type.Null()]),
    tripId: Type.Union([Type.String(), Type.Null()]),
    tripCode: Type.Union([Type.String(), Type.Null()]),
    reporterRole: Type.Union([Type.Literal("rider"), Type.Literal("driver")]),
    location: Type.Union([LatLng, Type.Null()]),
    createdAt: Type.String({ format: "date-time" }),
  }),
);

/** Staff acknowledged or resolved an incident: to the reporter (`user:#{id}`) and `admin:live`. */
export const SafetyIncidentUpdated = event(
  "safety.incident_updated",
  Type.Object({
    incidentId: Type.String(),
    status: SafetyIncidentStatus,
    /** pt-BR for the reporter: "A central está acompanhando sua corrida". */
    message: Type.String(),
  }),
);

export const ConfigChanged = event("config.changed", Type.Object({ keys: Type.Array(Type.String()) }));

/** Manual review outcome (phase 1), sent to `user:#{driverId}` and `admin:live`. */
export const DriverReviewDecided = event(
  "driver.review_decided",
  Type.Object({
    driverId: Type.String(),
    cityId: Type.String(),
    status: Type.Union([Type.Literal("approved"), Type.Literal("resubmission_requested"), Type.Literal("rejected")]),
    reason: Type.Optional(Type.String()),
    /** Document kinds the driver must send again. */
    resendKinds: Type.Optional(Type.Array(Type.String())),
  }),
);

/** Staff decided on a prova de vida video: to `user:#{driverId}` and `admin:live`. */
export const DriverLivenessDecided = event(
  "driver.liveness_decided",
  Type.Object({
    driverId: Type.String(),
    cityId: Type.String(),
    /** `rejected`: record a new video; `suspended`: not the person on the CNH, the account is suspended. */
    status: Type.Union([Type.Literal("approved"), Type.Literal("rejected"), Type.Literal("suspended")]),
    reason: Type.Optional(Type.String()),
  }),
);

/**
 * Staff decided on one document of an approved driver (Cadastros): to `user:#{driverId}` and `admin:live`.
 * `rejected`: send it again (with the reason); `blocksGo`: GO stays locked until a new one is approved.
 */
export const DriverDocumentDecided = event(
  "driver.document_decided",
  Type.Object({
    driverId: Type.String(),
    cityId: Type.String(),
    kind: Type.String(),
    status: Type.Union([Type.Literal("approved"), Type.Literal("rejected")]),
    reason: Type.Optional(Type.String()),
    blocksGo: Type.Boolean(),
  }),
);

/** A support message for the user (`user:#{id}`, from staff, bot or system) and for staff (`admin:support`). */
export const SupportMessageEvent = event("support.message", Type.Object({ ticketId: Type.String(), message: SupportMessageView }));

/** Ticket status or assignee changed: to the user (`user:#{id}`) and to staff (`admin:support`). */
export const SupportTicketUpdated = event(
  "support.ticket_updated",
  Type.Object({ ticketId: Type.String(), status: SupportTicketStatus, staffName: Type.Optional(Type.String()) }),
);

/**
 * A weekly payout item or an instant withdrawal ("Sacar agora via Pix") changed status, to
 * `user:#{driverId}`. Emitted by the payouts module (admin branch).
 */
export const PayoutUpdated = event(
  "payout.updated",
  Type.Object({
    id: Type.String({ description: "payout_items.id (also the withdrawal id)" }),
    kind: Type.Union([Type.Literal("weekly"), Type.Literal("instant")]),
    amountCents: Cents,
    status: Type.Union([Type.Literal("ready"), Type.Literal("held"), Type.Literal("sent"), Type.Literal("paid"), Type.Literal("failed"), Type.Literal("retrying")]),
    pixKeyMasked: Type.String(),
    failureReason: Type.Optional(Type.String()),
    paidAt: Type.Optional(Type.String({ format: "date-time" })),
    createdAt: Type.String({ format: "date-time" }),
  }),
);

/** The sharer's own view of a share changed (started, contacts updated, stopped), on `user:#{id}`. */
export const TripShareUpdated = event(
  "trip.share_updated",
  Type.Object({
    tripId: Type.String(),
    token: Type.String(),
    url: Type.String(),
    active: Type.Boolean(),
    contacts: Type.Array(Type.Object({ id: Type.String(), name: Type.String() })),
    /** Why it changed: the rider/driver, an automatic rule, or the trip ending. */
    reason: Type.Union([Type.Literal("manual"), Type.Literal("auto"), Type.Literal("stopped"), Type.Literal("ended")]),
  }),
);

/** Safety alert for the rider (`user:#{riderId}` and `trip:{id}`, plus `admin:live` for Segurança): the route deviated; contacts were texted. */
export const TripSafetyAlert = event(
  "trip.safety_alert",
  Type.Object({
    tripId: Type.String(),
    kind: Type.Literal("deviation"),
    /** pt-BR text for a banner / notification. */
    message: Type.String(),
    contactsNotified: Type.Integer(),
  }),
);

/**
 * A message from the ichegou team to one user (`user:#{id}`): a direct notice from the admin or one
 * chunk of a broadcast ("Notificar motoristas"). The notifier pushes it; open apps may show a banner.
 */
export const StaffNotice = event(
  "staff.notice",
  Type.Object({
    app: Type.Union([Type.Literal("rider"), Type.Literal("driver")]),
    title: Type.String({ maxLength: 80 }),
    body: Type.String({ maxLength: 400 }),
    /** Set when the notice is part of a broadcast (audit_log id). */
    broadcastId: Type.Optional(Type.String()),
  }),
);

const LiveDriver = Type.Object({
  driverId: Type.String(),
  status: DriverStatus,
  location: LatLng,
  heading: Type.Optional(Type.Number()),
  tripId: Type.Optional(Type.String()),
  categories: Type.Array(RideCategory),
  updatedAt: Type.String({ format: "date-time" }),
}, { $id: "LiveDriver" });

const LiveTrip = Type.Object({
  tripId: Type.String(),
  code: Type.String(),
  status: TripStatus,
  category: RideCategory,
  driverId: Type.Optional(Type.String()),
  pickup: LatLng,
  dropoff: LatLng,
  driverLocation: Type.Optional(LatLng),
  requestedAt: Type.String({ format: "date-time" }),
}, { $id: "LiveTrip" });

/**
 * Staff live map (Admin-06/07) on `admin:live`, one per city every few seconds. Positions and
 * ids only: names and contacts are fetched through audited admin endpoints.
 */
export const AdminLiveSnapshot = event(
  "admin.live_snapshot",
  Type.Object({ cityId: Type.String(), drivers: Type.Array(LiveDriver), trips: Type.Array(LiveTrip) }),
);
export type LiveDriver = Static<typeof LiveDriver>;
export type LiveTrip = Static<typeof LiveTrip>;

export const EVENT_SCHEMAS = [
  TripRequested,
  TripOffered,
  TripOfferClosed,
  OpenTripListed,
  OpenTripRemoved,
  TripStatusChanged,
  DriverLocation,
  ChatMessage,
  ChatRead,
  PixCharge,
  PaymentConfirmed,
  ConfigChanged,
  DriverReviewDecided,
  DriverLivenessDecided,
  DriverDocumentDecided,
  AdminLiveSnapshot,
  SupportMessageEvent,
  SupportTicketUpdated,
  PayoutUpdated,
  TripShareUpdated,
  TripSafetyAlert,
  StaffNotice,
  CardCharged,
  CardFailed,
  TipReceived,
  TripStopUpdated,
  TripCollectionUpdated,
  TripDriverApproaching,
  TripScheduledReminder,
  SafetyIncidentEvent,
  SafetyIncidentUpdated,
] as const satisfies readonly TObject[];

export type RealtimeEvent = Static<(typeof EVENT_SCHEMAS)[number]>;
export type EventType = RealtimeEvent["type"];
export type EventOf<T extends EventType> = Extract<RealtimeEvent, { type: T }>;
export type EventData<T extends EventType> = EventOf<T>["data"];

export const EVENT_SCHEMA_BY_TYPE = Object.fromEntries(
  EVENT_SCHEMAS.map((s) => [s.properties.type.const, s]),
) as unknown as { [K in EventType]: TObject };

/** Builds a typed event envelope. */
export function makeEvent<T extends EventType>(type: T, data: EventData<T>, now = new Date()): EventOf<T> {
  return { type, v: 1, id: Bun.randomUUIDv7(), occurredAt: now.toISOString(), data } as EventOf<T>;
}
